import { useEffect } from "react";
import { fetchLatestAnswerIfReady } from "./chatRecovery";

// Safety net for the non-streamed chat: if a bot reply is still pending past
// `timeoutMs`, poll the REST API for the persisted answer and hand it to
// `onRecovered`. This covers the case where the WebSocket completion frame never
// reaches the client (dropped/reconnected socket, missed frame) — the answer is
// already saved server-side, so the UI self-heals instead of being stuck on
// "Thinking" forever.
//
// `active` should be true only while a reply is awaited. `getChannelId` returns
// the current chat channel id at call time. `responseReceivedRef` lets us bail
// the moment the socket delivers normally.
export const useThinkingFallback = ({
  active,
  getChannelId,
  responseReceivedRef,
  onRecovered,
  onExhausted,
  timeoutMs = 12000,
  retries = 2,
  retryDelayMs = 5000,
}) => {
  useEffect(() => {
    if (!active) return undefined;

    let cancelled = false;
    let timer;
    let attempt = 0;

    const alreadyAnswered = () =>
      cancelled || (responseReceivedRef && responseReceivedRef.current);

    const tryRecover = async () => {
      if (alreadyAnswered()) return;
      attempt += 1;

      let answer = null;
      try {
        answer = await fetchLatestAnswerIfReady(getChannelId());
      } catch (err) {
        answer = null;
      }

      if (alreadyAnswered()) return;

      if (answer) {
        onRecovered(answer);
        return;
      }
      if (attempt <= retries) {
        timer = setTimeout(tryRecover, retryDelayMs);
      } else if (onExhausted) {
        onExhausted();
      }
    };

    timer = setTimeout(tryRecover, timeoutMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Only (re)arm when the waiting state flips; getChannelId/onRecovered read
    // refs and stable setters so they don't need to be dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
};
